import React from "react";

// ENG-2652: keyboard operability for the click targets that aren't buttons.
//
// The console had 126 `onClick` handlers on non-interactive elements and four of them were reachable
// without a mouse. A `<div onClick>` has no role, no tab stop and no key handler: a screen reader
// never announces it as actionable, and a keyboard user cannot reach it at all. Every drilldown on
// Overview and the Risk Report was in that state — the primary interaction of both surfaces.
//
// Two helpers, because the correct markup differs and using the wrong one makes things worse.

/** Enter and Space activate; everything else is left alone. Shared so the check can't drift. */
const activateOnKey = (onActivate: () => void) => (event: React.KeyboardEvent) => {
  if (event.key !== "Enter" && event.key !== " ") return;
  // Space scrolls the page and Enter can submit an enclosing form; neither is what the user meant.
  event.preventDefault();
  onActivate();
};

/** A visible focus ring. `:focus-visible`, so pointer users never see it. */
export const focusRingSx = (color: string) => ({
  "&:focus-visible": {
    outline: `2px solid ${color}`,
    outlineOffset: "-2px",
  },
});

/**
 * Props for an element that behaves as a button but isn't one — a `Box`, a `Typography`, a card.
 *
 * Gives it the button role, a tab stop, an accessible name, and Enter/Space. Use this anywhere
 * OUTSIDE a table.
 *
 * Not on a list row either, however much it looks like one. A button's children are presentational
 * and [label] replaces them, so the row's other cells go unannounced, and any control inside it
 * becomes a button within a button. Members & Seats did this until ENG-3354: every row read as
 * "Open {name}" and nothing else, and Enter on its ⋮ opened the member instead of the menu. See
 * [rowClickProps] for what a row does instead.
 *
 * [label] is required rather than optional: the whole failure being fixed is an element a screen
 * reader cannot describe, and an unnamed `role="button"` is announced as just "button", which is
 * barely an improvement.
 */
export const clickableProps = (onActivate: () => void, label: string) => ({
  role: "button",
  tabIndex: 0,
  "aria-label": label,
  onClick: onActivate,
  onKeyDown: activateOnKey(onActivate),
});

/**
 * Props for a clickable **table row**.
 *
 * Deliberately does NOT set `role="button"`. A `<tr>` carries an implicit `role="row"` that tells
 * assistive tech where the row sits in the table and which headers apply to its cells; overriding it
 * with `button` discards all of that and announces one opaque control instead of a row of labelled
 * data. That trade is a net loss — the row becomes reachable and simultaneously unreadable.
 *
 * It also sets no `aria-label`, for the same reason: on a row, a label can replace the announcement
 * of the cell contents, which is the information the user actually wants. The row keeps its content;
 * this adds only the tab stop and the key handler that make it operable.
 *
 * The stronger fix — a real `<button>` inside the first cell carrying the action — is better still,
 * and worth doing where a row has one obvious primary action. This is the change that makes every
 * existing drilldown usable without restructuring every table first. Members & Seats makes the
 * stronger fix (ENG-3354): [rowClickProps] on the row, a `RowOpenButton` around the name.
 */
export const clickableRowProps = (onActivate: () => void) => ({
  tabIndex: 0,
  onClick: onActivate,
  onKeyDown: activateOnKey(onActivate),
});

/**
 * The pointer half of a row whose keyboard and screen-reader path is a real button inside it (the
 * stronger fix above). No role, tab stop or key handler: the button is the row's one control, and
 * this only keeps the rest of the row clickable for a mouse.
 *
 * It ignores a click that did not happen inside the row's own DOM. React bubbles events along the
 * component tree, through portals, so a click in a menu or dialog that one of the row's children
 * renders (MUI puts both in `document.body`) still arrives here. On Members & Seats that made every
 * item in a row's ⋮ menu open the member as well, which navigated away before the dialog it had just
 * opened could show (ENG-3354).
 */
export const rowClickProps = (onActivate: () => void) => ({
  onClick: (event: React.MouseEvent<HTMLElement>) => {
    if (event.currentTarget.contains(event.target as Node)) onActivate();
  },
});

/**
 * `onKeyDown` for a native control — an `IconButton`, a `Checkbox` — nested inside a row or card
 * that handles Enter and Space itself.
 *
 * Stops those two keys at the control and prevents nothing: the control's own activation is the
 * browser default, and it is exactly what should happen. Without this the keys bubble to the row,
 * whose handler prevents the default and runs the ROW's action instead. That is how Enter on a
 * Groups ⋮ opened the members dialog rather than the menu, leaving Rename and Delete unreachable by
 * keyboard, and how Space on a Secure Sites category checkbox expanded the category rather than
 * toggling it (ENG-3354).
 */
export const stopActivationKeys = (event: React.KeyboardEvent) => {
  if (event.key === "Enter" || event.key === " ") event.stopPropagation();
};

/**
 * Props for a click target nested INSIDE another one — a drill-down number inside a clickable card.
 *
 * Stops propagation on the key handler as well as the click. Only doing it for clicks leaves the
 * keyboard path broken in a way that is easy to miss: Enter on the child runs the child's action and
 * then bubbles to the parent's `onKeyDown`, firing both. The Groups ⋮ was exactly that: ENG-2649
 * stopped its click from opening the row's dialog behind the menu, nothing stopped its keys, and
 * Enter opened the row instead of the menu until ENG-3354. A real button nested like that needs only
 * [stopActivationKeys], since its activation is native.
 *
 * Nesting interactive elements is not ideal markup. Where the layout allows it, prefer siblings.
 */
export const nestedClickableProps = (onActivate: () => void, label: string) => ({
  role: "button",
  tabIndex: 0,
  "aria-label": label,
  onClick: (event: React.MouseEvent) => {
    event.stopPropagation();
    onActivate();
  },
  onKeyDown: (event: React.KeyboardEvent) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    event.stopPropagation();
    onActivate();
  },
});

/**
 * ENG-3480: the media query for a touch screen. Keyed on the pointer, not the viewport width - a
 * phone held sideways and a tablet are both wider than any mobile breakpoint and both tapped.
 */
export const COARSE_POINTER = "@media (pointer: coarse)";

/**
 * A control's minimum size under a finger (ENG-3480). The console's pager arrows were 18px glyphs and
 * the Risk Report's category chips 24px tall. 40px rather than a platform's 44/48: it is what the rest
 * of this console already sizes its controls to, and it clears WCAG 2.5.8's 24px with room to spare.
 * Mouse users keep the compact size.
 *
 * The theme applies it to every IconButton and ToggleButton, and its height to every Button (ENG-3356).
 * A call site cannot opt out with a plain `minWidth`/`minHeight`/`height` in `sx`; only its own
 * `[COARSE_POINTER]` block in `sx` overrides the theme's.
 */
export const touchTargetSx = { [COARSE_POINTER]: { minWidth: 40, minHeight: 40 } } as const;
